<template>
  <v-container fluid class="event-detail pa-3"> 
    <!-- Event banner -->
    <v-card flat outlined class="mb-4 event-banner" :class="`banner--${event.type}`">
      <div class="banner-stripe" :class="`stripe--${event.type}`"></div>
      <v-card-text class="d-flex align-start pa-4" style="gap:14px">
        <div class="event-icon" :class="`event-icon--${event.type}`">
          <v-icon>{{ typeConfig.icon }}</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="d-flex align-center flex-wrap mb-1" style="gap:8px">
            <span class="subtitle-1 font-weight-medium">{{ event.title }}</span> 
          </div>
          <div class="d-flex flex-wrap caption grey--text" style="gap:12px">
            <span><v-icon x-small>mdi-map-marker-outline</v-icon> {{ event.location }}</span>
            <span><v-icon x-small>mdi-calendar</v-icon> {{ formatDate(event.timestamp) }}</span>
            <span><v-icon x-small>mdi-clock-outline</v-icon> {{ formatTime(event.timestamp) }}</span>
            <span><v-icon x-small>mdi-account-outline</v-icon> {{ event.driverName }}</span>
          </div>
          <div class="mt-1" style="font-family:monospace;font-size:11px;color:#9e9e9e">
            {{ event.ref }} · IMEI {{ event.imei }}
          </div>
        </div> 
      </v-card-text>
    </v-card>

    <v-row>
      <!-- Left column -->
      <v-col cols="12" md="12">

        <!-- Speed & acceleration metrics -->
        <v-card flat outlined class="mb-4">
          <v-card-title class="subtitle-2 font-weight-medium py-3 px-4">
            <v-icon left small color="grey">mdi-chart-line</v-icon>
            Speed &amp; acceleration
            <v-spacer/>
            <span class="caption grey--text">At time of event</span>
          </v-card-title>
          <v-divider/>
          <v-card-text class="pa-4">
            <v-row dense class="mb-4">
              <v-col v-for="m in metrics" :key="m.label" cols="6" sm="4">
                <div class="metric-card">
                  <div class="metric-label">{{ m.label }}</div>
                  <div class="metric-value" :class="m.danger ? 'red--text text--darken-2' : ''">
                    {{ m.value }}
                    <span class="metric-unit">{{ m.unit }}</span>
                  </div>
                  <div class="metric-sub caption grey--text">{{ m.sub }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- Chart -->
            <div style="position:relative;height:180px">
              
              <canvas ref="speedChart" height="300"></canvas>
              <!-- Speed limit annotation line is drawn by Chart.js plugin below -->
            </div>
            <div class="d-flex justify-space-between mt-1">
              <span
                v-for="(t, i) in event.timeHistory" :key="i"
                class="caption"
                :class="t.event ? 'red--text text--darken-2 font-weight-medium' : 'grey--text'"
              >{{ t.label }}</span>
            </div>
          </v-card-text>
        </v-card>

        <!-- GPS trail -->
        <v-card flat outlined>
          <v-card-title class="subtitle-2 font-weight-medium py-3 px-4">
            <v-icon left small color="grey">mdi-map-marker-path</v-icon>
            GPS trail
            <v-spacer/>
            <span class="caption grey--text">±5 min around event</span>
          </v-card-title>
          <v-divider/>
          <v-card-text class="pa-4">
            <!-- Map placeholder — swap for your map library (Leaflet, Google Maps, etc.) -->
            <div ref="mapContainer" id="mapContainer" style="height:230px;border-radius:8px;background:#f5f5f5;overflow:hidden"></div>

            <!-- Legend -->
            <div class="d-flex flex-wrap mt-3" style="gap:12px">
              <div v-for="l in mapLegend" :key="l.label"
                class="d-flex align-center caption grey--text" style="gap:5px">
                <div class="legend-dot" :style="l.style"></div>
                {{ l.label }}
              </div>
            </div>

            <v-divider class="my-3"/>

            <!-- Coordinates -->
            <div class="overline grey--text mb-2">Event coordinates</div>
            <v-row dense>
              <v-col v-for="c in coords" :key="c.key" cols="12" sm="6">
                <div class="d-flex justify-space-between py-1"
                  style="border-bottom:1px solid rgba(0,0,0,.06)">
                  <span class="caption grey--text">{{ c.key }}</span>
                  <span class="caption font-weight-medium" :class="c.color || ''">
                    {{ c.val }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-col>

      <!-- Right column -->
      <v-col cols="12" md="4"> 
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
const TYPE_CONFIG = {
  overspeed: { icon: 'mdi-gauge',              color: '#e24b4a' },
  stoppage:  { icon: 'mdi-clock-alert-outline', color: '#ef9f27' },
  geofence:  { icon: 'mdi-map-marker-alert',    color: '#1d9e75' },
}
import common from './../mixins/common.js';   
import L from "leaflet";
import { fetchEventDetail } from "@/services/nodeReports";

export default {
  name: 'EventDetail',

  data() {
    return {
      acking: false,
      chart: null, 
      map: null,
      // In production, load this from your API using $route.params.id
      event: {},
    }
  },

  computed: {
    typeConfig() {
      return TYPE_CONFIG[this.event.type] || TYPE_CONFIG.overspeed
    },

    statusColor() {
      return this.event.status === 'Open' ? 'error' : 'success'
    },

    metrics() {
      const e = this.event
      return [
        { label: 'Recorded speed', value: e.speed,        unit: 'km/h', danger: true,  sub: `+${Math.round((e.speed/e.speedLimit-1)*100)}% over limit` },
        { label: 'Speed limit',    value: e.speedLimit,   unit: 'km/h', danger: false, sub: e.location },
        { label: 'Peak speed',     value: e.peakSpeed,    unit: 'km/h', danger: true,  sub: 'During event' }, 
      ]
    },
 
    coords() {
      const e = this.event
      return [
        { key: 'Latitude',     val: parseFloat(e.lat).toFixed(4) + '° S' },
        { key: 'Longitude',    val: parseFloat(e.lng).toFixed(4) + '° E' },
        { key: 'Altitude',     val: parseInt(e.altitude).toLocaleString() + ' m' }, 
        { key: 'Heading',      val: `${e.heading}° NE` },
      ]
    },

    mapLegend() {
      return [
        { label: 'Start of window', style: 'background:#e1f5ee;border:1.5px solid #1d9e75' },
        { label: 'Event location',  style: 'background:#fcebeb;border:1.5px solid #e24b4a' },
        { label: 'End of window',   style: 'background:#e6f1fb;border:1.5px solid #185fa5' },
      ]
    },
  },


  async mounted() {
    await this.loadEvent(); 
    this.$nextTick(() => {
      this.initChart()
      this.initMap()
    })
  },

  beforeDestroy() {
    if (this.chart) this.chart.destroy()
  },
  mixins: [common], 
  methods: {
    async loadEvent() { 
      const rp = await fetchEventDetail(this.$route.query.id);
      if (rp.status == 200) {   
        this.event = rp.data;  
      }
    },
    
    initChart() {
      const ctx = this.$refs.speedChart
      if (!ctx || !window.Chart) return  
      const speeds = this.event.speedHistory
      const limit  = this.event.speedLimit 
      this.chart = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels: speeds.map(() => ''),
          datasets: [{
            label: "Speeds",
            data: speeds,
            borderWidth: 2,
            tension: 0.45,
            fill: false,
            backgroundColor: 'rgba(24,95,165,0.06)',
            pointRadius: speeds.map(v => v >= limit ? 4 : 0),
            pointBackgroundColor: speeds.map(v => v >= limit ? '#e24b4a' : '#185fa5'),
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            segment: {
              borderColor: ctx => ctx.p1.parsed.y >= limit ? '#e24b4a' : '#185fa5'
            }
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            // Speed limit annotation — requires chartjs-plugin-annotation
            annotation: {
              annotations: {
                limitLine: {
                  type: 'line',
                  yMin: limit, yMax: limit,
                  borderColor: 'rgba(226,75,74,0.55)',
                  borderWidth: 1.5,
                  borderDash: [5, 4],
                }
              }
            }
          },
          scales: {
            x: { display: false },
            y: {
              min: 40, max: 110,
              ticks: { callback: v => v + ' km/h', stepSize: 20, font: { size: 11 } },
              grid: { color: 'rgba(0,0,0,0.05)' },
              border: { display: false }
            }
          }
        }
      })
    },

    updateMap() {
      if (!this.map) {
        this.initMap();
        return;
      }

      const { lat, lng, trail } = this.event;

      if (this.marker) {
        this.map.removeLayer(this.marker);
      }

      if (this.trailLayer) {
        this.map.removeLayer(this.trailLayer);
      }

      this.marker = L.circleMarker([lat, lng], {
        radius: 8,
        color: '#e24b4a',
        fillColor: '#e24b4a',
        fillOpacity: 0.9
      }).addTo(this.map);

      if (trail && trail.length) {
        const coords = trail.map(p => [p.lat, p.lng]);

        this.trailLayer = L.polyline(coords, {
          color: '#185fa5',
          weight: 3
        }).addTo(this.map);

        this.map.fitBounds(
          this.trailLayer.getBounds(),
          { padding: [20, 20] }
        );
      } else {
        this.map.setView([lat, lng], 15);
      }
    },

    updateChart() {
      if (!this.chart) {
        this.initChart();
        return;
      }

      const speeds = this.event.speedHistory || [];
      const limit = this.event.speedLimit;

      this.chart.data.labels = speeds.map(() => '');

      this.chart.data.datasets[0].data = speeds;
      this.chart.data.datasets[0].pointRadius =
        speeds.map(v => v >= limit ? 4 : 0);

      this.chart.data.datasets[0].pointBackgroundColor =
        speeds.map(v => v >= limit ? '#e24b4a' : '#185fa5');

      if (
        this.chart.options.plugins &&
        this.chart.options.plugins.annotation
      ) {
        this.chart.options.plugins.annotation.annotations.limitLine.yMin = limit;
        this.chart.options.plugins.annotation.annotations.limitLine.yMax = limit;
      }

      this.chart.update();
    },

    initMap() { 
      this.map = L.map("mapContainer", { zoomControl: true }) 
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(this.map)

      this.updateMap();
    },

    formatDate(ts) {
      return new Date(ts).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
      })
    },

    formatTime(ts) {
      return new Date(ts).toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      })
    },
 
  },
  watch: { 
    '$route.query.id': {
      async handler(newId, oldId) { 
        await this.loadEvent(); 
        this.updateChart();
        this.updateMap();
      }
    }  
  }
}
</script>

<style scoped>
.event-banner { position: relative; overflow: hidden; }

.banner-stripe { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
.stripe--overspeed { background: #e24b4a; }
.stripe--stoppage  { background: #ef9f27; }
.stripe--geofence  { background: #1d9e75; }

.event-icon { width: 42px; height: 42px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.event-icon--overspeed { background: #fcebeb; color: #a32d2d; }
.event-icon--stoppage  { background: #faeeda; color: #854f0b; }
.event-icon--geofence  { background: #e1f5ee; color: #0f6e56; }

.metric-card { background: #f5f5f5; border-radius: 8px; padding: 12px 14px; }
.metric-label { font-size: 11px; color: #9e9e9e; text-transform: uppercase;
  letter-spacing: .04em; margin-bottom: 4px; }
.metric-value { font-size: 20px; font-weight: 500; line-height: 1.1; }
.metric-unit  { font-size: 13px; font-weight: 400; }
.metric-sub   { font-size: 11px; color: #9e9e9e; margin-top: 3px; }

.legend-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
</style>